import { useMutation } from '@apollo/client';
import { Editor } from '@tiptap/core';
import React, { useMemo, useRef } from 'react';
import { useLatest } from 'react-use';

import { useCompleteFragment } from '@eluve/apollo-client';
import { useTypingThrottle } from '@eluve/blocks';
import { Button, Stack, Text, cn, useToast } from '@eluve/components';
import { EluveEditor, EluveEditorHandle } from '@eluve/eluve-editor';
import {
  useAppointmentId,
  useAppointmentStatus,
  useIsAppointmentReadonly,
  useSnippetEditorExtension,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { pendingUnsavedChangesStore } from './components/AppointmentLastSaved';

const doctorInteractionNotesFragment = graphql(`
  fragment DoctorInteractionNotes on AppointmentDoctorInteractions {
    __typename
    appointmentId
    additionalNotes
    updatedAt
  }
`);

const insertAdditionalNotesMutation = graphql(`
  mutation insertDoctorAdditionalNotes(
    $appointmentId: uuid!
    $additionalNotes: String
  ) {
    insertAppointmentDoctorInteractionsOne(
      object: {
        appointmentId: $appointmentId
        additionalNotes: $additionalNotes
      }
      onConflict: {
        constraint: appointment_doctor_interactions_pkey
        updateColumns: [additionalNotes]
      }
    ) {
      __typename
      appointmentId
      additionalNotes
      updatedAt
    }
  }
`);

const AdditionalNotesContent: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const appointmentId = useAppointmentId();
  const [insertAdditionalNotes] = useMutation(insertAdditionalNotesMutation);
  const { snippetExtension, snippetCount } = useSnippetEditorExtension();

  const data = useCompleteFragment({
    fragment: doctorInteractionNotesFragment,
    key: { appointmentId },
    strict: false,
  });

  const isReadonly = useIsAppointmentReadonly();

  const additionalNotes = data?.additionalNotes ?? '';
  const latestAdditionalNotes = useLatest(additionalNotes);

  const appointmentStatus = useAppointmentStatus();

  const editorRef = useRef<EluveEditorHandle | null>(null);

  const updateNotes = useRef(async (notes: string) => {
    await insertAdditionalNotes({
      variables: {
        appointmentId,
        additionalNotes: notes,
      },
      onError: () => {
        toast.error(
          <Stack align="center" direction="horizontal" justify="between">
            <span>{t('appointmentPage.additionalNotes.updateFailed')}</span>
            <Button
              onClick={() => updateNotes.current(notes)}
              variant="destructive"
              className="float-end"
            >
              {t('appointmentPage.retry')}
            </Button>
          </Stack>,
          {
            id: 'edit-summary-error',
          },
        );
      },
    });

    pendingUnsavedChangesStore.send({ type: 'set', value: false });
  });

  const updateNotesThrottle = useTypingThrottle(updateNotes.current);

  const shouldHideAdditionalNotes = useIsFeatureFlagEnabled(
    'HIDE_ADDITIONAL_NOTES',
  );

  const onUpdate = useMemo(() => {
    return async (editor: Editor | undefined) => {
      if (
        latestAdditionalNotes.current ===
        getTiptapMarkdownContentFromEditor(editor)
      ) {
        return;
      }
      pendingUnsavedChangesStore.send({ type: 'set', value: true });
      editor && updateNotesThrottle(getTiptapMarkdownContentFromEditor(editor));
    };
  }, [latestAdditionalNotes, updateNotesThrottle]);

  if (
    appointmentStatus === 'NOT_STARTED' ||
    appointmentStatus === 'CANCELLED'
  ) {
    return null;
  }

  if (shouldHideAdditionalNotes) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-sm border border-bg-quaternary bg-transparent p-2 text-text-secondary outline-hidden hover:border-border-secondary focus:border-border-accent-quaternary',
        'my-4 rounded-lg border-border-secondary',
      )}
    >
      <Text inline size="xs" className="p-2">
        {t('appointment.signedSummary.additionalNotes')}
      </Text>
      <EluveEditor
        key={snippetCount}
        disabled={isReadonly}
        ref={editorRef}
        content={additionalNotes}
        onUpdate={onUpdate}
        className="px-0!"
        extensions={[snippetExtension]}
      />
    </div>
  );
};

export const AdditionalNotes = React.memo(AdditionalNotesContent);
